import { useMutation } from '@tanstack/react-query';

import {
  type AddCardDisputeEvidenceRequest,
  cardsAddCardDisputeEvidence,
  cardsGetCardDisputeDocument,
  cardsUploadCardDisputeDocument,
} from '@column/api';

import { downloadFile } from '~/util';

import { useCardDisputeMutationWrapper } from './useCardDisputeMutationWrapper';

export interface UploadCardDisputeDocumentVariables {
  file: File;
  idempotencyKey: string;
}

export interface UploadedCardDisputeDocument {
  id: string;
  description: string;
  size: number;
}

/** Upload through the generated multipart serializer without retaining the returned presigned URL. */
export const useUploadCardDisputeDocument = () =>
  useMutation<UploadedCardDisputeDocument, unknown, UploadCardDisputeDocumentVariables>({
    mutationFn: async ({ file, idempotencyKey }) => {
      const { data } = await cardsUploadCardDisputeDocument({
        body: { file, description: file.name },
        headers: { 'Idempotency-Key': idempotencyKey },
        throwOnError: true,
      });

      return { id: data.id, description: data.description, size: data.size };
    },
  });

export interface AddCardDisputeEvidenceVariables {
  evidence: AddCardDisputeEvidenceRequest;
}

export const useAddCardDisputeEvidence = ({
  disputeId,
  transactionId,
}: {
  disputeId: string;
  transactionId: string;
}) => {
  const wrapMutation = useCardDisputeMutationWrapper();

  return useMutation(
    wrapMutation(
      {
        mutationFn: async ({ evidence }: AddCardDisputeEvidenceVariables) => {
          const { data } = await cardsAddCardDisputeEvidence({
            body: evidence,
            path: { card_dispute_id: disputeId },
            throwOnError: true,
          });
          return data;
        },
      },
      () => ({ disputeId, transactionId })
    )
  );
};

export interface DownloadCardDisputeDocumentVariables {
  documentId: string;
  evidenceId: string;
}

/** Fetch a fresh 60-second URL for each user action and never put that URL in React Query state. */
export const useDownloadCardDisputeDocument = () =>
  useMutation<{ documentId: string }, unknown, DownloadCardDisputeDocumentVariables>({
    mutationFn: async ({ documentId }) => {
      const { data } = await cardsGetCardDisputeDocument({
        path: { document_id: documentId },
        query: { content_disposition: 'attachment' },
        throwOnError: true,
      });

      downloadFile(data.url, data.description || data.id);
      return { documentId };
    },
  });
